// Constants
import { HttpStatus, HttpStatusText } from "@ms/uno-constants/lib/local/HttpStatusConstants";
import { generateRouteFromDeepLink } from "@ms/uno-routing/lib/local/utilities/DeepLinkUtils";
// Base pre-fetcher
import { AbstractDataPreFetcher } from "../../base/preFetcher/AbstractDataPreFetcher";
// Teams SDK
import { app, authentication } from "@microsoft/teams-js-uno";
// Host adaptor
import { tryParseTabRoute } from "@ms/uno-hostadaptors/lib/local/utils/TeamsRouteUtils";
import { generateRouteFromLegacyDeepLink } from "@ms/uno-hostadaptors/lib/local/utils/LegacyTeamsDeepLinkUtils";
/**
 * Data pre-fetcher that is used to prefetch data for MetaOS app
 */
export class MetaOSAppDataPreFetcher extends AbstractDataPreFetcher {
    async setupHostDependencies() {
        if (!app.isInitialized()) {
            await app.initialize();
            const metaOSAppContext = await app.getContext();
            const userClickTime = metaOSAppContext.app.userClickTime;
            const markExtraData = userClickTime ? { userClickTime: String(userClickTime) } : undefined;
            this.mark("InitializedTeamsSDK", markExtraData);
        }
    }
    async getInitialDeeplinkRoute() {
        if (!app.isInitialized()) {
            return null;
        }
        const teamsContext = await app.getContext();
        const tabRoute = tryParseTabRoute(teamsContext.page.id, teamsContext.page.subPageId, teamsContext.page.frameContext, null);
        if (tabRoute) {
            return tabRoute;
        }
        const newPath = teamsContext.page.subPageId;
        return generateRouteFromDeepLink(newPath, null, generateRouteFromLegacyDeepLink);
    }
    async getAccessToken(resource) {
        // For Prod MS Graph resource, use retailservices graph token OBO endpoint
        if (resource === "https://graph.microsoft.com") {
            return this.preFetcherServices.getGraphTokenFromRetailService();
        }
        else {
            try {
                const authTokenRequestParams = {
                    resources: [resource],
                };
                const token = await authentication.getAuthToken(authTokenRequestParams);
                return token;
            }
            catch (e) {
                if (this.config["EnableErrorMessageLogging"]) {
                    // We just need to wrap this error into a failed request result
                    const errorText = "Could not get an access token: TeamsAppDataPreFetcher";
                    const failureResult = this.generateAjaxClientError(new Response(errorText, { status: HttpStatus.Unauthorized, statusText: HttpStatusText.Unauthorized }), errorText);
                    return Promise.reject(failureResult);
                }
                else {
                    // If error message logging is disabled, we throw the error directly
                    return Promise.reject(e);
                }
            }
        }
    }
    async getUserId() {
        if (!app.isInitialized()) {
            await this.setupHostDependencies();
        }
        const metaOSAppContext = await app.getContext();
        const user = metaOSAppContext.user;
        if (!user) {
            throw new Error("The current user is not authenticated");
        }
        return user.id;
    }
    async canPreFetch() {
        const metaOSAppContext = await app.getContext();
        // The app pre-fetcher should not pre-fetch data if the page is in background load state
        return !metaOSAppContext.page.isBackgroundLoad;
    }
    /**
     * Return an error object for a failure API call
     * @param response Response object
     * @param e an error that can be a string or an Error object
     */
    generateAjaxClientError(response, e) {
        const failureResult = {
            response: response,
            error: typeof e === "string" ? new Error(e) : e ?? null,
        };
        return failureResult;
    }
}
